import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import apiService from '../../services/apiServices';

// Async Thunks

// Fetch all transactions
export const fetchTransactions = createAsyncThunk(
  'transactions/fetchTransactions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.get('transactions'); // Replace with your API endpoint
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Error fetching transactions');
    }
  }
);

export const fetchTransactionsPaginated = createAsyncThunk(
  'transactions/fetchTransactionsPaginated',
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiService.get('transactions/paginated', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Error fetching transactions');
    }
  }
);

// Add a new transaction
export const addTransaction = createAsyncThunk(
  'transactions/addTransaction',
  async (transactionData, { rejectWithValue }) => {
    try {
      const response = await apiService.post('transactions', transactionData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Error adding transaction');
    }
  }
);

// Update a transaction
export const updateTransaction = createAsyncThunk(
  'transactions/updateTransaction',
  async ({ id, transactionData }, { rejectWithValue }) => {
    try {
      const response = await apiService.put(`transactions/${id}`, transactionData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Error updating transaction');
    }
  }
);

// Delete a transaction
export const deleteTransaction = createAsyncThunk(
  'transactions/deleteTransaction',
  async (id, { rejectWithValue }) => {
    try {
      await apiService.delete(`transactions/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Error deleting transaction');
    }
  }
);

// Slice
const transactionsSlice = createSlice({
  name: 'transactions',
  initialState: {
    transactions: [],
    loading: false,
    error: null,
    pagination: {
      totalRecords: 0,
      totalPages: 0,
      currentPage: 1
    }
  },
  reducers: {
    resetError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Transactions
      .addCase(fetchTransactions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTransactions.fulfilled, (state, action) => {
        state.loading = false;
        state.transactions = action.payload;
      })
      .addCase(fetchTransactions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchTransactionsPaginated.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTransactionsPaginated.fulfilled, (state, action) => {
        state.loading = false;
        state.transactions = action.payload.transactions;
        state.pagination = {
          totalRecords: action.payload.totalRecords,
          totalPages: action.payload.totalPages,
          currentPage: action.payload.currentPage
        };
      })
      .addCase(fetchTransactionsPaginated.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Add Transaction
      .addCase(addTransaction.fulfilled, (state, action) => {
        state.transactions.push(action.payload);
      })
      .addCase(addTransaction.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Update Transaction
      .addCase(updateTransaction.fulfilled, (state, action) => {
        const index = state.transactions.findIndex(
          (transaction) => transaction._id === action.payload._id
        );
        if (index !== -1) {
          state.transactions[index] = action.payload;
        }
      })
      .addCase(updateTransaction.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Delete Transaction
      .addCase(deleteTransaction.fulfilled, (state, action) => {
        state.transactions = state.transactions.filter(
          (transaction) => transaction._id !== action.payload
        );
      })
      .addCase(deleteTransaction.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { resetError } = transactionsSlice.actions;

export default transactionsSlice.reducer;
